import { FC, useEffect, useState } from "react";
import DashboardContentLayout from "../components/dashboard-content-layout";
import { useNavigate } from "react-router-dom";
import { AdRoutes } from "../../../constants/ad-routes";

import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Radio,
  RadioGroup,
  Slider,
  TextField,
  Typography,
} from "@mui/material";

import { Visibility, VisibilityOff } from "@mui/icons-material";

import { ControlledSelect, ControlledSelectOption, makeSxStyles } from "ui";
import { useFormik } from "formik";
import { toast } from "react-toastify";

import {
  adminPostDriverReqDtoSchema,
  useDriverControllerCreate,
  useVehicleControllerGetUnassignedVehicles,
} from "service";
import {
  AdminPostDriverReqDto,
  AdminVehicleResDto,
} from "service/api/estabrookSchemas";
import { queryKeyFn } from "service/api/estabrookContext";
import { queryClient } from "../../../query-client";
import useAddressAutocomplete from "../../../hooks/use-autocomplete";
import { z } from "zod";
import Navbar from "../../../components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "../../../shared/components/header-items/HeaderItems";
import useUnsavedChangesBlocker from "../../../shared/hooks/useUnsavedChangesBlocker";
import { generateRandomPassword } from "../../../utils/password-generator";
import { useRegionManagement } from "../../../hooks/use-region-management";
import RegionAssignment from "../../../components/region-assignment/region-assignment";

export type AdminPostDriverDto = {
  name: string;
  email: string;
  startingPoint: string;
  available: boolean;
  vehicleId: string | null;
  performanceTime: number;
  licensePlate: string | null;
  regionIds: string[];
};

enum RegistrationMethod {
  EMAIL = "email",
  MANUAL = "manual",
}

// Custom validation schema that extends the base schema with conditional validation
const createDriverValidationSchema = (
  registrationMethod: RegistrationMethod,
) => {
  const baseSchema = adminPostDriverReqDtoSchema
    .omit({
      email: true,
      username: true,
      password: true,
    })
    .extend({
      name: z.string().min(1, "Name is required"),
      startingPoint: z.string().min(1, "Starting point is required"),
    });

  if (registrationMethod === RegistrationMethod.EMAIL) {
    return baseSchema.extend({
      email: z.string().email("Valid email is required"),
      username: z.string().optional(),
      password: z.string().optional(),
    });
  } else {
    return baseSchema.extend({
      email: z.string().optional(),
      username: z
        .string()
        .regex(
          /^[A-Za-z0-9._]+$/,
          "Only letters, numbers, underscores, and periods are allowed.",
        )
        .min(4, "Enter a username with at least 4 characters."),
      password: z
        .string()
        .regex(
          /^(?=.*[A-Za-z])(?=.*\d)/,
          "Password must contain letters and numbers",
        )
        .min(8, "Password must be 8+ characters with letters and numbers"),
      vehicleId: z.string().optional(),
    });
  }
};

const AddDriverPage: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const [select, setSelect] = useState<ControlledSelectOption[]>([]);
  const [vehicles, setVehicles] = useState<AdminVehicleResDto[]>([]);
  const [showPassword, setShowPassword] = useState(false);

  const [radioValue, setRadioValue] = useState<RegistrationMethod>(
    RegistrationMethod.EMAIL,
  );

  // Create validation schema based on registration method
  const validationSchema = createDriverValidationSchema(radioValue);

  const { getPlacePredictions, placePredictions } = useAddressAutocomplete();

  const { mutate, isLoading } = useDriverControllerCreate({
    onSuccess: () => {
      toast.success("Driver Created Successfully");

      // Invalidate unassigned vehicles query since a vehicle might have been assigned
      queryClient.invalidateQueries(
        queryKeyFn({
          path: "/api/v1/admin/vehicles/unassigned",
          operationId: "vehicleControllerGetUnassignedVehicles",
          variables: {},
        }),
      );

      resetForm();

      if (radioValue === RegistrationMethod.MANUAL) {
        // Navigate with credentials for manual registration
        setTimeout(() => {
          navigate(AdRoutes.driverManagement, {
            state: {
              showCredentialsDialog: true,
              credentials: {
                username: values.username || "",
                password: values.password || "",
              },
            },
          });
        }, 10);
      } else {
        // For email registration, just navigate normally
        setTimeout(() => {
          navigate(AdRoutes.driverManagement);
        }, 10);
      }
    },
  });

  const { data: unassignedVehicles } =
    useVehicleControllerGetUnassignedVehicles({});

  const { regionOptions, checkConflictsAndProceed } = useRegionManagement({
    onConfirm: () => submitForm(),
  });

  const {
    values,
    handleChange,
    handleBlur,
    dirty,
    isValid,
    touched,
    errors,
    submitForm,
    resetForm,
    setFieldTouched,
    setFieldValue,
    validateForm,
  } = useFormik<AdminPostDriverReqDto & { regionIds: string[] }>({
    initialValues: {
      name: "",
      email: "",
      username: "",
      password: "",
      startingPoint: "",
      // licensePlate: "",
      available: true,
      vehicleId: "",
      performanceTime: 0,
      regionIds: [""],
    },
    validate: (values: AdminPostDriverReqDto & { regionIds: string[] }) => {
      try {
        validationSchema.parse(values);
        return {};
      } catch (error) {
        if (error instanceof z.ZodError) {
          const formikErrors: Record<string, string> = {};
          error.errors.forEach((err) => {
            if (err.path.length > 0) {
              formikErrors[err.path[0] as string] = err.message;
            }
          });
          return formikErrors;
        }
        return {};
      }
    },
    onSubmit: (values) => {
      const baseBody = {
        name: values.name,
        available: values.available,
        performanceTime: values.performanceTime,
        startingPoint: values.startingPoint,
        // Only include vehicleId if it's provided (for manual registration)
        ...(values.vehicleId && { vehicleId: values.vehicleId }),
        // Only include regionIds if it's manual registration and has valid regions
        ...(radioValue === RegistrationMethod.MANUAL &&
          values.regionIds.filter((id) => id !== "").length > 0 && {
            regionIds: values.regionIds.filter((id) => id !== ""),
          }),
      };

      const requestBody =
        radioValue === RegistrationMethod.EMAIL
          ? {
              ...baseBody,
              email: values.email ? values.email.toLowerCase() : undefined,
            }
          : {
              ...baseBody,
              username: values.username!.toLowerCase(),
              password: values.password,
            };

      mutate({
        body: requestBody,
      });
    },
  });

  useUnsavedChangesBlocker(dirty);

  // Re-validate form when registration method changes
  useEffect(() => {
    // Clear validation errors for fields that are not relevant to the current method
    if (radioValue === RegistrationMethod.EMAIL) {
      setFieldValue("username", "");
      setFieldValue("password", "");
      setFieldValue("vehicleId", "");
      setFieldValue("regionIds", [""]);
      setFieldTouched("username", false);
      setFieldTouched("password", false);
      setFieldTouched("vehicleId", false);
      setFieldTouched("regionIds", false);
    } else {
      setFieldValue("email", "");
      setFieldTouched("email", false);
    }
    // Trigger re-validation after clearing fields
    validateForm();
  }, [radioValue, setFieldValue, setFieldTouched, validateForm]);

  const marks = [
    {
      value: -100,
      label: "-100%",
    },
    {
      value: 0,
      label: "0%",
    },
    {
      value: 100,
      label: "100%",
    },
  ];

  function valuetext(value: number) {
    return `${value}`;
  }

  useEffect(() => {
    if (unassignedVehicles?.data && unassignedVehicles?.data) {
      setSelect(
        unassignedVehicles?.data.reduce(
          (acc: ControlledSelectOption[], vehicle: AdminVehicleResDto) => {
            if (vehicle && vehicle.available) {
              acc.push({
                label: vehicle.licensePlate,
                value: vehicle.id,
              });
            }
            return acc;
          },
          [] as ControlledSelectOption[],
        ),
      );
      setVehicles(unassignedVehicles?.data);
    }
  }, [unassignedVehicles]);

  const filterSelectedVehicle = (value: string) => {
    const selVehicle = vehicles.filter((item) => item.id === value);
    setFieldValue("vehicleId", selVehicle[0].id);
  };

  // Check if license plate (vehicleId) is selected to determine region assignment availability
  const isLicensePlateSelected =
    values.vehicleId && values.vehicleId.trim() !== "";

  // Handle license plate change - clear regions if license plate is removed
  const handleLicensePlateChange = (value: string) => {
    setFieldValue("vehicleId", value);
    setFieldTouched("vehicleId", true);

    // If license plate is being cleared and regions are selected, clear regions
    if (!value || value.trim() === "") {
      const hasSelectedRegions = values.regionIds.some(
        (id) => id && id.trim() !== "",
      );
      if (hasSelectedRegions) {
        setFieldValue("regionIds", [""]);
        setFieldTouched("regionIds", false);
      }
    }

    if (value) {
      filterSelectedVehicle(value);
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generateRandomPassword();
    setFieldValue("password", newPassword);
    setFieldTouched("password", true);
    // Trigger validation after setting the new password
    setTimeout(() => validateForm(), 0);
  };

  const onCreateDriver = () => {
    // For manual registration, check for region conflicts
    if (radioValue === RegistrationMethod.MANUAL) {
      checkConflictsAndProceed(values.regionIds);
    } else {
      submitForm();
    }
  };

  const handleRegionChange = (newRegionIds: string[]) => {
    setFieldValue("regionIds", newRegionIds);
    setFieldTouched("regionIds", true);
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Driver Management",
                url: AdRoutes.driverManagement,
              },
              {
                label: "Add New Driver",
                url: AdRoutes.addDriver,
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={isLoading || !dirty || !isValid}
              variant="contained"
              onClick={onCreateDriver}
              data-testid="add-driver-button"
            >
              Add Driver
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>
      <Box sx={styles.driverDetailContainer}>
        <Typography>Name</Typography>
        <TextField
          id="name"
          type="text"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          helperText={touched.name && errors.name}
          error={touched.name && !!errors.name}
          inputProps={{ maxLength: 30 }}
          margin="dense"
          variant="outlined"
          fullWidth
        />
        <Typography>Starting Point</Typography>
        <Autocomplete
          freeSolo
          id="startingPoint"
          value={values.startingPoint}
          filterOptions={(x) => x}
          options={placePredictions.map((prediction) => {
            return { label: prediction.description, value: prediction };
          })}
          onChange={(_evt, value) => {
            if (value && typeof value === "object") {
              setFieldValue("startingPoint", String(value.label));
              setFieldTouched("startingPoint");
            }
          }}
          onBlur={() => setFieldTouched("startingPoint")}
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={(evt) => {
                getPlacePredictions({ input: evt.target.value?.trim() });
              }}
            />
          )}
        />
        <Typography>Availability</Typography>
        <Checkbox
          id="available"
          checked={values.available}
          sx={{ justifySelf: "flex-start" }}
          onChange={handleChange}
        />
        <Typography>Performance Time</Typography>
        <Slider
          id="performanceTime"
          marks={marks}
          valueLabelDisplay="on"
          getAriaValueText={valuetext}
          onChange={(_, val) => {
            setFieldValue("performanceTime", val);
            setFieldTouched("performanceTime", true);
          }}
          sx={styles.customSliderStyle}
          value={values.performanceTime}
          min={-100}
          max={100}
          defaultValue={0}
        />
      </Box>
      <Divider sx={{ mt: "40px", mb: "40px" }} />
      <Box sx={styles.registrationMethodContainer}>
        <Typography>Registration Method</Typography>
        <RadioGroup
          defaultValue={RegistrationMethod.EMAIL}
          value={radioValue}
          onChange={(event) => {
            setRadioValue(event.target.value as RegistrationMethod);
          }}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <FormControlLabel
            value={RegistrationMethod.EMAIL}
            control={<Radio />}
            sx={styles.registrationMethodRadio}
            label={
              <>
                <Typography variant="bodyLarge">
                  Send an email invite
                </Typography>
                <Typography variant="bodyMedium">
                  Driver will get an invite to register.
                </Typography>
              </>
            }
          />
          <FormControlLabel
            value={RegistrationMethod.MANUAL}
            control={<Radio />}
            sx={styles.registrationMethodRadio}
            label={
              <>
                <Typography variant="bodyLarge">
                  Manually set username and password
                </Typography>
                <Typography variant="bodyMedium">
                  Create login credentials now and share them with the driver
                  directly.
                </Typography>
              </>
            }
          />
        </RadioGroup>
      </Box>
      <Box sx={styles.driverDetailContainer}>
        {radioValue === RegistrationMethod.EMAIL && (
          <>
            <Typography>Email</Typography>
            <TextField
              id="email"
              type="text"
              value={values.email}
              inputProps={{ maxLength: 50 }}
              onChange={handleChange}
              onBlur={handleBlur}
              helperText={touched.email && errors.email}
              error={touched.email && !!errors.email}
              margin="dense"
              variant="outlined"
              fullWidth
            />
          </>
        )}
        {radioValue === RegistrationMethod.MANUAL && (
          <>
            <Typography>Username</Typography>
            <TextField
              id="username"
              type="text"
              name="username"
              value={values.username}
              inputProps={{ maxLength: 50 }}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-autocomplete="none"
              helperText={touched.username && errors.username}
              error={touched.username && !!errors.username}
              margin="dense"
              variant="outlined"
              autoComplete="new-password"
              fullWidth
            />
            <Typography>Password</Typography>
            <Box sx={styles.passwordContainer}>
              <TextField
                id="password"
                aria-autocomplete="none"
                type={showPassword ? "text" : "password"}
                value={values.password}
                inputProps={{ maxLength: 50 }}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.password && errors.password}
                error={touched.password && !!errors.password}
                margin="dense"
                variant="outlined"
                autoComplete="new-password"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {!showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button onClick={handleGeneratePassword}>Generate</Button>
            </Box>
            <Typography>License Plate</Typography>
            <ControlledSelect
              placeholderSelectEnabled={false}
              placeholder="License Plate"
              options={select}
              value={values.vehicleId}
              onChange={handleLicensePlateChange}
              data-testid="license-plate"
            />
            <RegionAssignment
              regionIds={values.regionIds}
              regionOptions={regionOptions}
              onRegionChange={handleRegionChange}
              disabled={!isLicensePlateSelected}
              disabledReason={
                !isLicensePlateSelected
                  ? "Please select a license plate first"
                  : undefined
              }
            />
          </>
        )}
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  driverDetailContainer: {
    mt: "40px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "center",
  },
  divider: {
    mt: "40px",
  },
  customSliderStyle: {
    "& .MuiSlider-valueLabel": {
      width: "28px",
      height: "34px",
      backgroundColor: "transparent",
      backgroundImage: "url('../marker-indicator.svg')",
      backgroundPosition: "center center",
      backgroundSize: "cover",
      color: "#00344E",
    },

    "& .MuiSlider-track, & .MuiSlider-thumb": {
      color: "#8ACEFF",
    },

    "& .MuiSlider-markLabel": {
      color: "#C1C7CE",
    },
  },
  registrationMethodContainer: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "16px",
    maxWidth: "50%",
    alignItems: "flex-start",
  },
  registrationMethodRadio: {
    "& .MuiFormControlLabel-label": {
      display: "flex",
      flexDirection: "column",
    },
  },
  passwordContainer: {
    position: "relative",
    "& .MuiButton-root": {
      position: "absolute",
      left: "100%",
      marginLeft: "16px",
      top: "50%",
      transform: "translateY(-50%)",
    },
  },
}));

export default AddDriverPage;
